import React from 'react';
import fhirpath from 'fhirpath';
import { FiEdit2, FiPrinter } from 'react-icons/fi';

import { useAuth } from '@/features/auth/providers/AuthProvider/hooks';
import { useEnterpriseContext } from '@/features/enterprise/contexts/EnterpriseContext';

import Information from '@/components/Information';
import IconButton from '@/components/IconButton';
import Markdown from '@/components/Markdown';
import Editor from '@/components/Editor';

import { FhirPedidoType } from '@/features/enterprise/models/types';
import { clinicalDocumentPdf } from '@/features/pdf/documents/ClinicalDocumentLayout';
import DocumentPageEncaminhamento from '@/features/pdf/documents/DocumentPageEncaminhamento';
import { getSolicitacaoObrigatoriaMotivo } from '@/features/recursos/utils/solicitacaoObrigatoria';

import { useExecucaoContext } from '@/features/execucao/providers/ExecucaoProvider';
import Feature from '@/features/features/components/Feature';
import OptionIntegracao from '@/types/OptionIntegracao';
import SolicitacoesAPI from '@/features/prescricao-livre/services/SolicitacoesAPI';
import {
  Container,
  Header,
  HeaderTitle,
  Actions,
  CollapsedContent,
  Description,
  TextSpan,
  DescriptionContainer,
  ButtonHover,
  CollapseButton,
} from './styles';

interface BoxEncaminhamentoEditavelProps {
  option: OptionIntegracao;
  selected?: boolean;
  disabled?: boolean;
  isOpen?: boolean;
}

interface EditorState {
  hoverContainer: boolean;
  status: null | 'disabled' | 'editing' | 'disabled-edited' | 'edited';
}

const BoxEncaminhamentoEditavel: React.FC<BoxEncaminhamentoEditavelProps> = ({
  option,
  selected,
  disabled,
  isOpen,
}) => {
  const contentRef = React.createRef<HTMLDivElement>();

  const [contentHeight, setContentHeight] = React.useState<number>(0);
  const [isOpenedDetails, setIsOpenedDetails] = React.useState(isOpen || false);
  const [printing, setPrinting] = React.useState(false);
  const solicitacaoObrigatoriaMotivo = React.useMemo(
    () =>
      getSolicitacaoObrigatoriaMotivo(
        'encaminhamento',
        option.solicitacao_obrigatoria_motivo,
      ),
    [option.solicitacao_obrigatoria_motivo],
  );

  const { user } = useAuth();
  const [{ encounter }] = useEnterpriseContext();

  const [, { dispatchAlterarStatus }] = useExecucaoContext();

  const encaminhamento = React.useMemo(() => option.pedido, [option]);

  const canEditIndicacao = React.useMemo(() => {
    return encaminhamento.status === 'draft' && !disabled;
  }, [encaminhamento, disabled]);

  const [
    encaminhamentoEditavel,
    setEncaminhamentoEditavel,
  ] = React.useState<FhirPedidoType>(encaminhamento);

  const titulo = React.useMemo(() => {
    return fhirpath.evaluate(
      encaminhamentoEditavel,
      'ServiceRequest.code.text | ServiceRequest.code.coding.display',
    )[0];
  }, [encaminhamentoEditavel]);

  const indicacao = React.useMemo(() => {
    return fhirpath.evaluate(
      encaminhamentoEditavel,
      'ServiceRequest.reasonCode.text',
    )[0] as string;
  }, [encaminhamentoEditavel]);

  const [editorState, setEditorState] = React.useState<EditorState>({
    hoverContainer: false,
    status: encaminhamento.reportedBoolean ? 'edited' : null,
  });

  const hasDescription = React.useMemo(() => !!indicacao, [indicacao]);

  const toggleDetails = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      if (hasDescription) {
        setIsOpenedDetails(prev => {
          if (prev) {
            setEditorState(prevStateIndicacao => ({
              ...prevStateIndicacao,
              hoverContainer: false,
            }));
          }

          return !prev;
        });
      }
    },
    [hasDescription],
  );

  const imprimirEncaminhamento = React.useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();

      try {
        setPrinting(true);

        await clinicalDocumentPdf(DocumentPageEncaminhamento, {
          user,
          resource: encaminhamentoEditavel as fhir4.ServiceRequest,
          encounter: encounter.data,
        });
      } catch (error) {
        throw new Error(`Erro ao gerar PDF do encaminhamento: ${error}`);
      } finally {
        setPrinting(false);
      }
    },
    [user, encaminhamentoEditavel, encounter.data],
  );

  const dispatchCustomData = React.useCallback(
    async (pedido: FhirPedidoType) => {
      const retorno = await SolicitacoesAPI.update({
        id: option.value.toString(),
        tipo: 'encaminhamento',
        resource: pedido as fhir4.ServiceRequest,
        opts: {
          session_id: option.sessionId,
        },
      });

      if (retorno) {
        setEditorState(prev => ({
          ...prev,
          status: 'edited',
        }));

        setEncaminhamentoEditavel(retorno.pedido as FhirPedidoType);
      }
    },
    [option.sessionId, option.value],
  );

  const onCancelClick = React.useCallback(() => {
    setEditorState(prev => {
      return {
        ...prev,
        status: encaminhamentoEditavel.reportedBoolean ? 'edited' : null,
        hoverContainer: false,
      };
    });
  }, [encaminhamentoEditavel]);

  const onConfirmClick = React.useCallback(
    (content: string) => {
      dispatchCustomData({
        ...encaminhamentoEditavel,
        ...{
          reasonCode: { text: content },
        },
      });
    },
    [dispatchCustomData, encaminhamentoEditavel],
  );

  const onChangeEditorText = React.useCallback(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [contentRef]);

  const onMouseOver = React.useCallback(() => {
    if (canEditIndicacao) {
      setEditorState(prev => ({ ...prev, hoverContainer: true }));
    }
  }, [canEditIndicacao]);

  const onMouseLeave = React.useCallback(() => {
    if (canEditIndicacao) {
      setEditorState(prev => ({ ...prev, hoverContainer: false }));
    }
  }, [canEditIndicacao]);

  const onEditClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
      if (canEditIndicacao) {
        if (selected) {
          event.stopPropagation();
        }
        setEditorState(prev => ({
          ...prev,
          status: 'editing',
          hoverContainer: false,
        }));
      }
    },
    [canEditIndicacao, selected],
  );

  const handleEditing = React.useCallback(() => {
    dispatchAlterarStatus({ status: 'on-hold' });
  }, [dispatchAlterarStatus]);

  const handleEdited = React.useCallback(() => {
    dispatchAlterarStatus({ status: 'active' });
  }, [dispatchAlterarStatus]);

  React.useEffect(() => {
    if (editorState.status === 'editing') {
      handleEditing();
    } else {
      handleEdited();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorState.status]);

  React.useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [contentRef]);

  return (
    <Container>
      <Header>
        <HeaderTitle highlight={isOpenedDetails}>{titulo}</HeaderTitle>

        <Actions>
          {(editorState.status === 'edited' ||
            editorState.status === 'disabled-edited') && (
            <TextSpan>editado</TextSpan>
          )}
          {isOpenedDetails && canEditIndicacao && (
            <ButtonHover onMouseOver={onMouseOver} onMouseLeave={onMouseLeave}>
              <IconButton
                tooltip="Editar encaminhamento"
                onClick={onEditClick}
                icon={<FiEdit2 />}
              />
            </ButtonHover>
          )}
          <Feature name="IMPRIMIR_ENCAMINHAMENTO">
            <IconButton
              disabled={printing}
              tooltip="Imprimir"
              onClick={imprimirEncaminhamento}
              icon={<FiPrinter />}
            />
          </Feature>

          {hasDescription && (
            <CollapseButton
              collapsed={isOpenedDetails}
              onClick={toggleDetails}
            />
          )}
        </Actions>
      </Header>

      {option.solicitacao_obrigatoria && (
        <Information type="warning" style={{ margin: '12px 0 0' }}>
          {solicitacaoObrigatoriaMotivo}
        </Information>
      )}

      <CollapsedContent
        ref={contentRef}
        style={{
          marginLeft: editorState.status === 'editing' ? -32 : 0,
          maxHeight: isOpenedDetails ? contentHeight : 0,
        }}
      >
        {indicacao && editorState.status === 'editing' ? (
          <Editor
            content={indicacao}
            onChange={onChangeEditorText}
            onCancelClick={onCancelClick}
            onConfirmClick={onConfirmClick}
          />
        ) : (
          <DescriptionContainer
            highlight={editorState.hoverContainer}
            disabled={disabled}
            selected={selected}
            onClick={onEditClick}
            onMouseOver={onMouseOver}
            onMouseLeave={onMouseLeave}
          >
            <Description>
              <span style={{ textOverflow: 'ellipsis' }}>
                <Markdown text={indicacao} />
              </span>
            </Description>
          </DescriptionContainer>
        )}
      </CollapsedContent>
    </Container>
  );
};

export default BoxEncaminhamentoEditavel;
